import { z } from 'zod';
import { ProductLayoutSchema } from '../settings/product-layout-schema';

export const AdminCampaignSchema = z.object({
  id: z.string(),
  active: z.boolean().default(true),
  name: z.string().default('Nueva Campaña'),
  spreadsheetUrl: z.string().optional(),
  spreadsheetId: z.string().optional(),
  sheetGid: z.string().optional(),
  webhookUrl: z.string().optional(),
  adAccountId: z.string().optional(),
  fieldMapping: z.record(z.string(), z.string()).default({}), // Original Header -> Friendly Name
  visibleFields: z.array(z.string()).default([]), // List of Original Headers to show
  statusMapping: z.record(z.string(), z.string()).default({
    CREATED: 'NUEVO',
    CONTACTED: 'CONTACTADO',
    QUALIFIED: 'CALIFICADO',
    VERIFYING_PAYMENT: 'VERIFICANDO',
    SOLD: 'VENDIDO',
  }),
  adsetAliases: z.record(z.string(), z.string()).default({}), // Alias for internal Meta AdSet IDs
  adAliases: z.record(z.string(), z.string()).default({}), // Alias for internal Meta Ad IDs
  notificationUserId: z.string().optional(), // ID of the user (manager/pro) to notify

  // --- FIRMA-AI: THE BEAST CONFIG ---
  autoResponseEnabled: z.boolean().default(false),
  aiContext: z.string().optional(), // "Who are we" (Location, Trust factors)
  aiTraining: z.string().optional(), // "The Hormozi Salesman" (Tactics, Goal)

  // Dynamic Knowledge Manifest
  // Key: AdSet Alias or Form Label (e.g., "licencia_3ra", "rcv_moto")
  servicesKnowledge: z.record(z.string(), z.object({
    price: z.number().optional(),
    requirements: z.array(z.string()).default([]),
    description: z.string().optional(),
  })).default({}),

  formFieldConfig: z.object({
    header: z.string().optional(), // The header in CSV to look for
    labels: z.record(z.string(), z.string()).default({}), // Map of CSV values to Friendly Labels
  }).optional(),
});

export const AdminSettingsSchema = z.object({
  plan: z.enum(['SOLO', 'SALON', 'ELITE', 'GROWTH']).default('SOLO'),
  isFounder: z.boolean().default(false), // Honorary status
  expiresAt: z.any().nullable(), // The master source of truth for access (Timestamp)
  accountStatus: z.enum(['active', 'suspended', 'cancelled']).default('active'),
  features: z.object({
    marketingEnabled: z.boolean().default(false),
    whatsappEnabled: z.boolean().default(false),
    apiAccess: z.boolean().default(false),
  }).default({ marketingEnabled: false, whatsappEnabled: false, apiAccess: false }),
  marketingCampaigns: z.array(AdminCampaignSchema).default([]),
  analyticsCache: z.object({
    lastFetch: z.any().nullable(), // Firestore Timestamp
    data: z.record(z.string(), z.any()).nullable(), // Aggregated PostHog payload
    metaAds: z.object({
      lastFetchAt: z.any().nullable(),
      campaigns: z.array(z.any()).default([]), // specific campaign metrics
      accountData: z.record(z.string(), z.any()).default({}), // aggregate metrics
    }).optional(),
  }).optional(),
  capabilities: z.object({
    allowedProductLayouts: z.array(ProductLayoutSchema).default(['standard']),
  }).default({
    allowedProductLayouts: ['standard'],
  }),
  updatedAt: z.any(), // Audit timestamp
});

export type AdminSettings = z.infer<typeof AdminSettingsSchema>;
